import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import AddButton, { DraftButton } from "../../../../components/buttons/button";
import CloseIcon from "../../../../assets/icons/close-icon";
import successToast from "../../../../components/helper-components/success-toast";
import errorToast from "../../../../components/helper-components/error-toast";
import { api } from "../../../../redux/apis/api";
import {
  warehouseBinDefaultItemsAddAction,
  warehouseBinDefaultItemsListAction,
} from "../../../../redux/actions/inventory-action";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

const AddDefaultItemsModal = ({
  isModalOpen = false,
  setIsModalOpen,
  bin,
  onAdded = () => {},
}) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) {
      setSearchTerm("");
      setResults([]);
      setSelectedItems([]);
      return;
    }
    if (!bin?.bin_no_id) return;
    // Preload the bin's current default items so they show up already
    // checked/selected — submitting replaces the whole set (see
    // warehouseBinDefaultItemsAddAction), so without this any items not
    // re-selected here would silently get removed from the bin.
    setLoadingCurrent(true);
    dispatch(
      warehouseBinDefaultItemsListAction(
        { bin_no_id: bin.bin_no_id },
        (res) => {
          setSelectedItems(res?.data ?? []);
          setLoadingCurrent(false);
        },
        () => {
          setLoadingCurrent(false);
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, bin?.bin_no_id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = searchTerm.trim();
    if (query.length < MIN_QUERY) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(
          `/product/get?field=item_name_search&value=${encodeURIComponent(
            query
          )}`
        );
        const rows = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setResults(rows);
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchTerm]);

  const isSelected = (item_id) =>
    selectedItems?.some((item) => item?.item_id === item_id);

  const toggleItem = (item) => {
    if (isSelected(item.item_id)) {
      setSelectedItems((prev) =>
        prev.filter((sel) => sel.item_id !== item.item_id)
      );
    } else {
      setSelectedItems((prev) => [...prev, item]);
    }
  };

  const handleSubmit = () => {
    if (selectedItems.length === 0) {
      errorToast("Select at least one item");
      return;
    }
    setLoading(true);
    dispatch(
      warehouseBinDefaultItemsAddAction(
        {
          bin_no_id: bin?.bin_no_id,
          item_ids: selectedItems.map((item) => item.item_id),
        },
        () => {
          setLoading(false);
          successToast("Default items saved successfully");
          setIsModalOpen(false);
          onAdded();
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsModalOpen(false)}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-full max-w-2xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              <div className="flex justify-between px-6 py-5">
                <h2 className="fw_500">
                  Manage Default Items{" "}
                  {bin?.bin_location_no ? `— ${bin.bin_location_no}` : ""}
                </h2>
                <button
                  type="button"
                  className="close-button bg-transparent border-none p-0 hover:bg-787878"
                  onClick={() => setIsModalOpen(false)}
                >
                  <CloseIcon />
                </button>
              </div>

              <div className="px-6">
                <input
                  type="text"
                  autoFocus
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search item by name…"
                  className="w-full border border-787878 rounded-md p-2 fs_14"
                />

                <div className="mt-3 max-h-[16rem] overflow-y-auto border border-787878 rounded-md">
                  {searchTerm.trim().length < MIN_QUERY ? (
                    <p className="text-667085 fs_12 p-3">
                      Type at least {MIN_QUERY} characters to search
                    </p>
                  ) : searching ? (
                    <p className="text-667085 fs_12 p-3">Searching…</p>
                  ) : results.length === 0 ? (
                    <p className="text-667085 fs_12 p-3">
                      No items match “{searchTerm.trim()}”
                    </p>
                  ) : (
                    results.map((item) => (
                      <label
                        key={item.item_id}
                        className="flex items-center gap-3 px-3 py-2 border-b border-787878 last:border-b-0 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected(item.item_id)}
                          onChange={() => toggleItem(item)}
                        />
                        <span className="fs_14 flex-1">
                          {item.item_name}
                          {item.item_code ? (
                            <span className="text-667085 fs_12">
                              {" "}
                              #{item.item_code}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))
                  )}
                </div>

                {loadingCurrent ? (
                  <p className="text-667085 fs_12 mt-3">
                    Loading current default items…
                  </p>
                ) : selectedItems.length > 0 ? (
                  <div className="mt-3">
                    <p className="fs_12 fw_600 text-667085 mb-2">
                      Default Items ({selectedItems.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItems.map((item) => (
                        <span
                          key={item.item_id}
                          className="flex items-center gap-2 fs_12 px-3 py-1 rounded-full border border-787878"
                        >
                          {item.item_name}
                          <button
                            type="button"
                            onClick={() => toggleItem(item)}
                            className="fw_600"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="px-6 flex justify-between gap-4 py-5 mt-3">
                <DraftButton
                  onclkFn={() => setIsModalOpen(false)}
                  text="Cancel"
                />
                <AddButton
                  loading={loading || loadingCurrent}
                  onclkFn={handleSubmit}
                  type="button"
                  className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                  text="Save Default Items"
                  prefixIcon={false}
                />
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddDefaultItemsModal;
